import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { InferPetitionScopeLocation } from './api.graphql';
import type { InferPetitionScopeLocationMutation, InferPetitionScopeLocationMutationVariables } from './api.graphql';

type Input = { mainPrompt: string; personalStory?: string };

/**
 * The parsed success payload. The GraphQL selection deliberately omits the resolved place text
 * (name/city/adminArea/country) and the model's free-text `reasoning`: this is a background
 * analytics signal and that content is PII-adjacent / unbounded, so it must not flow into tracking.
 */
type InferScopeLocationResult = Extract<
	InferPetitionScopeLocationMutation['inferPetitionScopeLocation'],
	{ __typename: 'InferPetitionScopeLocationSuccess' }
>;

// Bounded set of tracked error reasons — the typed-error arm throws the reason enum as its message,
// and anything else (transport / GraphQL failure) collapses to TRANSPORT_ERROR so the tracked
// `scope_inference_error_reason` stays low-cardinality. Mirrors the literal union declared for that
// property in `@change-corgi/tracking-properties`.
const ERROR_REASONS = [
	'FORBIDDEN',
	'INVALID_JSON_RESPONSE',
	'NOT_FOUND',
	'OTHER',
	'RATE_LIMIT',
	'UNAUTHORIZED',
] as const;
type ScopeInferenceErrorReason = (typeof ERROR_REASONS)[number] | 'TRANSPORT_ERROR';

function toTrackedErrorReason(message: string): ScopeInferenceErrorReason {
	return (ERROR_REASONS as readonly string[]).includes(message)
		? (message as ScopeInferenceErrorReason)
		: 'TRANSPORT_ERROR';
}

/**
 * Thin wrapper over the middle-end `inferPetitionScopeLocation` mutation, tracking its own success and
 * error the same way `generateAiPetitionDraft`'s `api.ts` does. Returns `null` when there's no prompt
 * to infer from (no call, no tracking). Throws on the typed-error arm and on transport failure so the
 * caller can discriminate success vs. failure; callers that treat this as best-effort (the SAP
 * background call) are responsible for swallowing the rejection.
 */
export async function inferPetitionScopeLocation(
	input: Input,
	{ gql: { fetch } }: UtilityContext,
	track: TrackingFn,
): Promise<InferScopeLocationResult | null> {
	if (!input.mainPrompt) return null;

	try {
		const { data } = await fetch<InferPetitionScopeLocationMutation, InferPetitionScopeLocationMutationVariables>({
			query: InferPetitionScopeLocation,
			// Matches the mutation's @restrictPath directive; required for the call to resolve.
			path: '/infer-petition-scope-location',
			variables: { input },
			rejectOnError: true,
		});

		const response = data.inferPetitionScopeLocation;

		// Throw the bare error-reason enum as the message so the catch below can track it as a bounded,
		// low-cardinality value. Transport failures reject earlier from `fetch` with their own error.
		if (response.__typename !== 'InferPetitionScopeLocationSuccess') throw new Error(response.reason);

		void track('sap_scope_inference_completed', {
			inferred_scope: response.scope,
			inferred_admin_level: response.adminLevel,
			scope_confidence: response.scopeConfidence,
			inferred_target_kind: response.targetKind,
			names_place_in_text: response.namesPlaceInText,
			inferred_location_source: response.location.source,
			inferred_location_resolved: response.location.resolved,
			inferred_location_confidence: response.location.confidence,
		});

		return response;
	} catch (e: unknown) {
		const message = e instanceof Error ? e.message : '';
		void track('sap_scope_inference_error', { scope_inference_error_reason: toTrackedErrorReason(message) });
		throw e;
	}
}
